import { gqlClient } from 'services/api/gql/gqlClient';
import { ValidateCampaignReferral } from 'constants/gql/queries/advocacy/validateCampaignReferral.gql';
import { ERROR } from 'components/Campaigns/Referrer/constants';

// RMS errorKey / legacy debugInfo.code → UFE ERROR.*
const RMS_CODE_TO_ERROR = {
    'cmpref.svc.campaign.referrer.registration.already.registered': ERROR.ERR_CAMP_REFEREE_ALREADY_REGISTERED,
    'cmpref.svc.campaign.referrer.registration.maxcount.rchd': ERROR.ERR_CAMP_REF_MAX_COUNT_RCHD,
    'cmpref.svc.campaign.expired': ERROR.ERR_CAMP_REF_CMP_EXPIRED,
    'cmpref.svc.campaign.referrer.self.registration.not.allowed': ERROR.ERR_CAMP_REFERRER_SELF_REGISTRATION_NOT_ALLOWED
};

const KNOWN_ERROR_CODES = new Set(Object.values(ERROR));

// Fallback used only when extensions are unavailable; recovers the case from message text.
const RMS_MESSAGE_SUBSTRINGS = [
    { fragment: 'already been redeemed by another friend', errorCode: ERROR.ERR_CAMP_REF_MAX_COUNT_RCHD },
    { fragment: 'Campaign has expired', errorCode: ERROR.ERR_CAMP_REF_CMP_EXPIRED },
    { fragment: 'promo codes have been claimed', errorCode: ERROR.ERR_CAMP_REF_MAX_COUNT_RCHD },
    { fragment: 'already signed up', errorCode: ERROR.ERR_CAMP_REFEREE_ALREADY_REGISTERED },
    { fragment: 'referrer.self.registration.not.allowed', errorCode: ERROR.ERR_CAMP_REFERRER_SELF_REGISTRATION_NOT_ALLOWED }
];

function classifyRmsError(err) {
    // Preferred: authoritative errorKey/errorCode preserved on the exception via preserveErrorExtensions.
    const extensions = err?.extensions || err?.errors?.[0]?.extensions;

    if (extensions) {
        if (RMS_CODE_TO_ERROR[extensions.errorKey]) {
            return RMS_CODE_TO_ERROR[extensions.errorKey];
        }

        if (KNOWN_ERROR_CODES.has(extensions.errorCode)) {
            return extensions.errorCode;
        }
    }

    // Legacy: debugInfo.code, when present.
    const structuredCode = err?.errors?.[0]?.extensions?.debugInfo?.code;

    if (structuredCode && RMS_CODE_TO_ERROR[structuredCode]) {
        return RMS_CODE_TO_ERROR[structuredCode];
    }

    // GraphQLException puts the flattened RMS text on `.cause`, not `.message`.
    const causeText = typeof err?.cause === 'string' ? err.cause : err?.cause?.message;
    const message = causeText || err?.message || err?.errors?.[0]?.message || '';
    const match = RMS_MESSAGE_SUBSTRINGS.find(entry => message.includes(entry.fragment));

    return match?.errorCode || ERROR.CUSTOM_ADVOCACY_DOWN;
}

/**
 * Validates a BAF/FnF campaign referral against RMS via the ValidateCampaignReferral GraphQL query.
 * Confirms the referrer/campaign combination is active, resolves the referee's enrollment status,
 * and issues an enrollment token used by the subsequent addToPromotion mutation. Response shape is
 * adapted to match what RwdAdvocacy.ctrlr expects from the legacy REST wrapper: GraphQL enum
 * `FNF` → mixed-case `FnF`, `enrollmentToken` → `referralToken`, and `clientPromoStartDate` /
 * `clientExpiryDate` → `startDate` / `expirationDate`.
 *
 * @param {Object} payload - Referral validation payload.
 * @param {string} payload.referrerCode - Rouge member's referrer code from the share link.
 * @param {string} payload.campaignCode - Campaign identifier (e.g. `hsebaf2025`).
 * @param {string} payload.refereeUsaId - Signed-in referee's BI account ID (empty for anonymous).
 * @param {string} payload.checksum - Signed URL checksum from the share link.
 * @returns {Promise<Object>} Adapted campaign details ready for the ctrlr to consume.
 * @throws {{ errors: [{ errorCode: string }] }} A classified `ERROR.*` code on GraphQL failure.
 */
async function validateCampaignReferral({ referrerCode, campaignCode, refereeUsaId, checksum }) {
    const { country } = Sephora.renderQueryParams;
    const requestedTimestamp = new Date().toISOString();

    try {
        const data = await gqlClient.query({
            headers: { 'x-source': 'ufe' },
            // Keep RMS error extensions on the thrown exception for classifyRmsError.
            preserveErrorExtensions: true,
            operationName: 'ValidateCampaignReferral',
            query: ValidateCampaignReferral,
            variables: {
                input: {
                    referrerCode,
                    campaignCode,
                    country,
                    channel: 'DIGITAL',
                    device: 'WEB',
                    refereeUsaId,
                    checksum,
                    requestedTimestamp
                }
            }
        });

        const result = data?.validateCampaignReferral;

        // Empty payload → stranded loading screen; coerce to CUSTOM_ADVOCACY_DOWN.
        if (!result) {
            throw { errors: [{ errorCode: ERROR.CUSTOM_ADVOCACY_DOWN }] };
        }

        return {
            ...result,
            campaignType: result.campaignType === 'FNF' ? 'FnF' : result.campaignType,
            referralToken: result.enrollmentToken || '',
            startDate: result.clientPromoStartDate || null,
            expirationDate: result.clientExpiryDate || null
        };
    } catch (err) {
        const errorCode = classifyRmsError(err);
        // GraphQLException wraps the real backend text on `.cause`; `err.message` is the generic wrapper.
        const rawMessage = (typeof err?.cause === 'string' ? err.cause : err?.cause?.message) || err?.errors?.[0]?.message || err?.message;
        Sephora.logger.warn('[advocacy/validateCampaignReferral] GraphQL error:', errorCode, rawMessage);

        // OPT-23304: carry the RMS message so the enhanced view can surface the per-campaign discount.
        throw { errors: [{ errorCode, message: rawMessage }] };
    }
}

export default validateCampaignReferral;
